import { createTheme } from '@mui/material/styles';

// New color scheme
const colors = {
  keppel: '#37bc9b',
  eucalyptus: '#47ddb2',  
  aquamarine: '#7df4d0',
  yellowSea: '#f9ad03',
  saffron: '#f9c33e',
  salomie: '#ffd878',
  sasquatchSocks: '#ff3f7a',
  strawberry: '#ff5b8d',
  tickleMePink: '#fe84a9',
  cerulean: '#1faae8',
  summerSky: '#30c4f2',
  turquoiseBlue: '#5bd9fc',
  mirage: '#191e32',
  darkMirage: '#121528',
  licorice: '#30364b',
  mariner: '#466099',
  blueGray: '#6e90cc',
  sazerac: '#f7ddc4',
  albescentWhite: '#f7e7d5',
  merino: '#f7efe5',
  catskillWhite: '#f2f4f9',
  whiteLilac: '#f7f8fa',
};

export const theme = createTheme({
  palette: {
    primary: {
      light: colors.sasquatchSocks,
      main: colors.sasquatchSocks,
      dark: '#e03367', // Darker version of Sasquatch Socks
      contrastText: '#ffffff',
    },
    secondary: {
      light: colors.saffron,
      main: colors.yellowSea,
      dark: '#ce9003', // Darker version of Yellow Sea
      contrastText: '#ffffff',
    },
    error: {
      light: colors.tickleMePink,
      main: colors.sasquatchSocks,
      dark: '#d32f64',
    },
    warning: {
      light: colors.salomie,
      main: colors.saffron,
      dark: '#cc9c33',
    },
    info: {
      light: colors.turquoiseBlue,
      main: colors.cerulean,
      dark: '#1a8dc0',
    },
    success: {
      light: colors.aquamarine,
      main: colors.eucalyptus,
      dark: '#39b395',
    },
    background: {
      default: colors.mirage,
      paper: colors.darkMirage, // Darker paper background for better contrast
    },
    text: {
      primary: '#ffffff',
      secondary: colors.catskillWhite,
    },
    action: {
      disabledBackground: 'rgba(255, 63, 122, 0.3)', // Better visibility for disabled state
      disabled: 'rgba(255, 255, 255, 0.6)', // Better contrast for disabled text
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.6)',
            backgroundColor: 'rgba(255, 63, 122, 0.3)',
          },
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        outlined: {
          borderColor: 'rgba(255, 63, 122, 0.5)',
          '&:hover': {
            borderColor: colors.sasquatchSocks,
            backgroundColor: 'rgba(255, 63, 122, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          backgroundColor: 'rgba(25, 30, 50, 0.5)', // Semi-transparent dark background
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'rgba(25, 30, 50, 0.5)', // Semi-transparent dark background
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        },
        elevation3: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          backgroundColor: colors.mirage,
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: 'rgba(255, 255, 255, 0.9)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: 'rgba(25, 30, 50, 0.8)', // Darker header for table
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        },
        body: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 63, 122, 0.08)', // Light sasquatch socks for hover
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 63, 122, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(255, 63, 122, 0.2)',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.sasquatchSocks,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.sasquatchSocks,
          },
          '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '& input': {
              color: 'rgba(255, 255, 255, 0.4)',
            },
          },
        },
        notchedOutline: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: colors.sasquatchSocks,
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.4)',
          },
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-disabled': {
            '& + .MuiSwitch-track': {
              opacity: 0.3,
            },
          },
        },
        track: {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          opacity: 1,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.6)',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.8)',
        },
        selectIcon: {
          color: 'rgba(255, 255, 255, 0.5)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.mirage,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: colors.mirage,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 63, 122, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(255, 63, 122, 0.5)',
            },
          },
        },
      },
    },
  },
}); 